import React, { useEffect, useRef, useState } from "react";
import { formatQuantity } from "../utils/quantity";

// A type-to-filter replacement for a plain <select> of products - built by
// hand (not <input list> + <datalist>) because mobile browser support for
// datalist's filtering UI is inconsistent (notably poor on iOS, uneven
// across Android WebViews), and this exists specifically to be reliable on
// the varied low-end phones cashiers actually use.
export default function ProductSearchSelect({ products, value, onChange, currency, placeholder = "Search for a product..." }) {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);

  // Keeps the field's displayed text in sync whenever `value` changes from
  // OUTSIDE this component (e.g. a barcode scan calling setSelectedProductId
  // directly) - otherwise the input would show stale or blank text while a
  // real product is actually selected.
  useEffect(() => {
    if (!value) {
      return;
    }
    const selected = products.find((p) => String(p.id) === String(value));
    if (selected) {
      setQuery(selected.name);
    }
  }, [value, products]);

  const filtered = products.filter((p) => p.name?.toLowerCase().includes(query.trim().toLowerCase()));

  const selectProduct = (product) => {
    if (product.quantity_on_hand <= 0) return;
    onChange(String(product.id));
    setQuery(product.name);
    setIsOpen(false);
  };

  return (
    <div className="product-search-select">
      <input
        type="text"
        value={query}
        placeholder={placeholder}
        onFocus={() => setIsOpen(true)}
        onBlur={() => setIsOpen(false)}
        onChange={(e) => {
          setQuery(e.target.value);
          setIsOpen(true);
          // Typing invalidates whatever was previously selected - "Add to
          // cart" must never fire against a stale, no-longer-matching
          // product, same guarantee a native <select>'s value always gives.
          onChange("");
        }}
      />
      {isOpen && (
        <div className="product-search-dropdown">
          {filtered.length === 0 && <div className="product-search-empty">No matching products.</div>}
          {filtered.map((p) => {
            const outOfStock = p.quantity_on_hand <= 0;
            return (
              <div
                key={p.id}
                className={`product-search-option${outOfStock ? " disabled" : ""}`}
                // onMouseDown (not onClick) + preventDefault: the input's
                // onBlur fires before a click would, which would close this
                // dropdown before the click ever registers. This is the
                // standard fix for that race, instead of a setTimeout hack.
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectProduct(p);
                }}
              >
                {p.name} ({currency(p.selling_price)}, {formatQuantity(p.quantity_on_hand, p.unit_of_measure)} in stock)
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
